import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import fallbackPlayer from '../../assets/graphics/fallback-player.svg';
import styleSheet from '../styles/playerModal.module.scss';
import { getImageUrl, onErrorPlayerImage } from '../../helpers/images';
import TickIcon from '../../components/shared-components/icons/TickIcon';

const styles = styleSheet.locals || {};

const OFFSET = 72;
const PLAYER_IMAGE_WIDTH = 84;

const POSITION_MAP = {
    GK: 'Goalkeeper',
    DEF: 'Defender',
    MID: 'Midfielder',
    MID2: 'Forward',
    FWD: 'Forward'
};

/**
 * PlayerSelector component displays a list of players for selection.
 *
 * @param {object} props - The props for the PlayerSelector component.
 * @param {Array} props.players - Array of player objects.
 * @param {Array} props.selectedPlayers - Array of selected player IDs.
 * @param {string} props.selectedPosition - Selected position
 * @param {Function} props.onSelect - Callback when a player is selected.
 * @param {Function} props.onClose - Callback when the selector is closed.
 * @returns {Element} The rendered PlayerSelector component.
 */
function PlayerSelector({
    players,
    selectedPlayers,
    selectedPosition,
    onSelect,
    onClose
}) {
    const positions = ['Goalkeeper', 'Defender', 'Midfielder', 'Forward'];
    const wrapperRef = useRef(null);
    const sectionRefs = useRef({});

    useEffect(() => {
        const targetPosition = POSITION_MAP[selectedPosition];
        const targetRef = sectionRefs.current[targetPosition];
        const wrapperElement = wrapperRef.current;

        if (targetRef && wrapperElement) {
            const wrapperRect = wrapperElement.getBoundingClientRect();
            const targetRect = targetRef.getBoundingClientRect();
            const scrollOffset = targetRect.top - wrapperRect.top - OFFSET;

            wrapperElement.scrollTo({
                top: scrollOffset,
                behavior: 'auto'
            });
        }
    }, [selectedPosition]);

    return (
        <>
            <style>{styleSheet.toString()}</style>
            <div className={styles.playerModal} part="ts-player-modal">
                <div ref={wrapperRef} className={styles.playerModalWrapper}>
                    <div className={styles.playerModalHeader}>
                        <div className={styles.playerModalTitle}>
                            Select player
                        </div>
                        <button
                            className={styles.playerModalCancel}
                            onClick={onClose}
                        >
                            Cancel
                        </button>
                    </div>

                    {positions.map((position) => {
                        const filteredPlayers = players.filter(
                            (player) => player.position === position
                        );
                        if (filteredPlayers.length === 0) {
                            return null;
                        }
                        return (
                            <div
                                key={position}
                                ref={(el) =>
                                    (sectionRefs.current[position] = el)
                                }
                                className={styles.playerModalBlock}
                            >
                                <div className={styles.playerModalPosition}>
                                    {position}s
                                </div>
                                <div className={styles.playerModalList}>
                                    {filteredPlayers.map((player) => {
                                        const isSelected =
                                            selectedPlayers.includes(
                                                player.player_id
                                            );

                                        const isExternalImage =
                                            player?.external_image_url;

                                        const imageUrl = isExternalImage
                                            ? player?.external_image_url
                                            : getImageUrl(
                                                  player?.image_url,
                                                  PLAYER_IMAGE_WIDTH
                                              );

                                        return (
                                            <button
                                                key={player.player_id}
                                                onClick={() =>
                                                    onSelect(player.player_id)
                                                }
                                                className={`${styles.playerModalPlayer} ${
                                                    isSelected
                                                        ? styles.playerModalPlayerSelected
                                                        : ''
                                                }`}
                                            >
                                                <img
                                                    src={
                                                        imageUrl ||
                                                        fallbackPlayer
                                                    }
                                                    alt={player.display_name}
                                                    className={
                                                        styles.playerModalPlayerImage
                                                    }
                                                    onError={onErrorPlayerImage}
                                                />
                                                <span
                                                    className={
                                                        styles.playerModalPlayerName
                                                    }
                                                >
                                                    {player.display_name}
                                                </span>

                                                {isSelected && (
                                                    <span
                                                        className={
                                                            styles.playerModalTick
                                                        }
                                                    >
                                                        <TickIcon />
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </>
    );
}

PlayerSelector.propTypes = {
    players: PropTypes.array.isRequired,
    selectedPlayers: PropTypes.array.isRequired,
    selectedPosition: PropTypes.string.isRequired,
    onSelect: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired
};

export default PlayerSelector;
